/**
 * Shared egg shape utilities.
 * Provides consistent egg Bezier path for canvas clipping, CSS clip-path, and visual outlines.
 */

/**
 * Draw an egg-shaped clip path on a canvas 2D context.
 * Call ctx.clip() after this to restrict drawing to the egg area.
 *
 * @param {CanvasRenderingContext2D} ctx - Canvas 2D context
 * @param {number} cx - Center X coordinate
 * @param {number} cy - Center Y coordinate
 * @param {number} rx - Horizontal radius
 * @param {number} ry - Vertical radius
 */
export function drawEggClipPath(ctx, cx, cy, rx, ry) {
  ctx.beginPath();
  // Top of the egg (narrower)
  ctx.moveTo(cx, cy - ry);
  // Top-right curve
  ctx.bezierCurveTo(cx + rx * 0.75, cy - ry, cx + rx, cy - ry * 0.4, cx + rx, cy);
  // Bottom-right curve (wider)
  ctx.bezierCurveTo(cx + rx, cy + ry * 0.6, cx + rx * 0.55, cy + ry, cx, cy + ry);
  // Bottom-left curve
  ctx.bezierCurveTo(cx - rx * 0.55, cy + ry, cx - rx, cy + ry * 0.6, cx - rx, cy);
  // Top-left curve back to top
  ctx.bezierCurveTo(cx - rx, cy - ry * 0.4, cx - rx * 0.75, cy - ry, cx, cy - ry);
  ctx.closePath();
}

/**
 * Evaluate a cubic Bezier at parameter t.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const u = 1 - t;
  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3;
}

/**
 * Generate CSS clip-path polygon string for an egg shape.
 * Samples points from the same Bezier curves used by drawEggClipPath
 * to ensure the CSS clip-path matches the canvas egg exactly.
 *
 * @param {number} pointsPerCurve - Points sampled per Bezier segment (default 18)
 * @returns {string} CSS clip-path value, e.g., "polygon(50% 0%, ...)"
 */
export function getEggClipPathCSS(pointsPerCurve = 18) {
  // Use percentage-based center and radii matching getEggDimensions
  const padding = 0.08;
  const cxPct = 50;
  const cyPct = 50;
  const rxPct = (0.5 - padding) * 100;
  const ryPct = (0.5 - padding) * 100;

  // 4 Bezier curves matching drawEggClipPath, in percentage coordinates
  const curves = [
    // Top-right: (cx, cy-ry) -> (cx+rx, cy)
    {
      x: [cxPct, cxPct + rxPct * 0.75, cxPct + rxPct, cxPct + rxPct],
      y: [cyPct - ryPct, cyPct - ryPct, cyPct - ryPct * 0.4, cyPct],
    },
    // Bottom-right: (cx+rx, cy) -> (cx, cy+ry)
    {
      x: [cxPct + rxPct, cxPct + rxPct, cxPct + rxPct * 0.55, cxPct],
      y: [cyPct, cyPct + ryPct * 0.6, cyPct + ryPct, cyPct + ryPct],
    },
    // Bottom-left: (cx, cy+ry) -> (cx-rx, cy)
    {
      x: [cxPct, cxPct - rxPct * 0.55, cxPct - rxPct, cxPct - rxPct],
      y: [cyPct + ryPct, cyPct + ryPct, cyPct + ryPct * 0.6, cyPct],
    },
    // Top-left: (cx-rx, cy) -> (cx, cy-ry)
    {
      x: [cxPct - rxPct, cxPct - rxPct, cxPct - rxPct * 0.75, cxPct],
      y: [cyPct, cyPct - ryPct * 0.4, cyPct - ryPct, cyPct - ryPct],
    },
  ];

  const points = [];
  for (const curve of curves) {
    for (let i = 0; i < pointsPerCurve; i++) {
      const t = i / pointsPerCurve;
      const px = bezierPoint(t, curve.x[0], curve.x[1], curve.x[2], curve.x[3]);
      const py = bezierPoint(t, curve.y[0], curve.y[1], curve.y[2], curve.y[3]);
      points.push(`${px.toFixed(2)}% ${py.toFixed(2)}%`);
    }
  }

  return `polygon(${points.join(', ')})`;
}

/**
 * Get the egg dimensions relative to a container size.
 * Returns egg center and radii for consistent sizing.
 *
 * @param {number} containerWidth - Container width in pixels
 * @param {number} containerHeight - Container height in pixels
 * @returns {{ cx: number, cy: number, rx: number, ry: number }}
 */
export function getEggDimensions(containerWidth, containerHeight) {
  const padding = 0.08;
  const rx = containerWidth * (0.5 - padding);
  const ry = containerHeight * (0.5 - padding);
  const cx = containerWidth / 2;
  const cy = containerHeight / 2;

  return { cx, cy, rx, ry };
}
